import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, History, Scale, TrendingDown, TrendingUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SiteFooter } from "@/components/SiteFooter";
import PageMeta from "@/components/PageMeta";
import { TokenCard } from "@/components/TokenCard";
import { computePickPerformance, formatPct, EXPECTANCY_EXPLAINER } from "@/lib/pickStats";


/** Ledger start — the date append-only logging actually began. */
const LEDGER_START = "26 August 2026";

interface PickRow {
  id: string;
  pick_type: "daily" | "gem";
  source_row_id: string;
  revision_of: string | null;
  revision_number: number;
  token_symbol: string | null;
  token_id: string | null;
  entry_score: number | null;
  entry_price_usd: number | null;
  entry_price_aud: number | null;
  rating: string | null;
  payload: any;
  published_at: string | null;
  recorded_at: string;
  status: "pending" | "win" | "loss" | "expired";
  current_pct: number | null;
  peak_pct: number | null;
  trough_pct: number | null;
  resolved_at: string | null;
  resolution_price_aud: number | null;
  last_checked_at: string | null;
}

const formatDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
    : "—";

const STATUS_STYLES: Record<string, string> = {
  win: "bg-green-500/15 text-green-400 border-green-500/30",
  loss: "bg-red-500/15 text-red-400 border-red-500/30",
  expired: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  pending: "bg-muted text-foreground border-border",
};

const STATUS_LABEL: Record<string, string> = {
  win: "Win",
  loss: "Loss",
  expired: "Expired (no win)",
  pending: "Open",
};

const TrackRecord = () => {
  const [picks, setPicks] = useState<PickRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data: pickRows } = await supabase
        .from("pick_history" as any)
        .select("*")
        .order("recorded_at", { ascending: false })
        .limit(500);

      setPicks((pickRows ?? []) as unknown as PickRow[]);
      setLoading(false);
    })();
  }, []);

  const dailyPicks = picks.filter((p) => p.pick_type === "daily");
  const gemPicks = picks.filter((p) => p.pick_type === "gem");

  const stats = useMemo(() => {
    const resolved = picks.filter((p) => p.status !== "pending");
    const losses = resolved.filter((p) => p.status === "loss").length;
    const expired = resolved.filter((p) => p.status === "expired").length;
    const open = picks.length - resolved.length;
    const perf = computePickPerformance(picks);
    return { wins: perf.wins, losses, expired, open, resolved: resolved.length, hitRate: perf.winRate, perf };
  }, [picks]);


  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <PageMeta
        title="Track Record — GoToCoins Daily Picks & Gems Performance"
        description="Public, revision-tracked record of every GoToCoins daily pick and undervalued gem call, with entry prices, one published resolution rule, and win/loss outcomes."
        path="/track-record"
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "GoToCoins Public Track Record",
          description: "Transparent performance record for daily crypto picks and undervalued gems.",
          author: { "@type": "Organization", name: "GoToCoins" },
          publisher: {
            "@type": "Organization",
            name: "GoToCoins",
            logo: { "@type": "ImageObject", url: "https://gotocoin.au/pwa-512x512.png" },
          },
          mainEntityOfPage: "https://gotocoin.au/track-record",
        }}
      />
      <header className="border-b border-border/60 bg-card/40 backdrop-blur sticky top-0 z-30">
        <div className="container mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" /> GoToCoins
          </Link>
          <div className="ml-auto text-sm text-muted-foreground">Track Record</div>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 py-10 max-w-5xl">
        <section className="mb-8">
          <h1 className="type-page-title flex items-center gap-3">
            <History className="w-7 h-7 text-primary" />
            Public Track Record
          </h1>
          <p className="mt-3 text-muted-foreground leading-relaxed max-w-3xl">
            Every Daily Pick and GEM Pick published from{" "}
            <strong className="text-foreground">{LEDGER_START}</strong> onward — including revisions, in the order they
            happened. No filtering, no cherry-picking, wins and losses both. Each entry is scored against one published
            rule (below) and resolved automatically every day. Nothing before this date is shown: earlier picks were
            never written to an append-only log with a verified entry price, so we won't reconstruct them.
          </p>
        </section>

        {/* The rule — published in plain language so it can be argued with. */}
        <Card className="mb-8 border-primary/30 bg-primary/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Scale className="w-4 h-4 text-primary" />
              The resolution rule (v1)
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-3">
            <p>
              Entry is the AUD price at the moment the pick is published — not a price we choose later. From that entry,
              a bot checks the live price once a day and applies exactly this:
            </p>
            <ul className="space-y-1.5">
              <li>
                <strong className="text-green-400">Win</strong> — the price is <strong>+15% or better</strong> vs entry
                at any daily check within 7 days.
              </li>
              <li>
                <strong className="text-red-400">Loss</strong> — the price is <strong>-10% or worse</strong> vs entry at
                any daily check within 7 days.
              </li>
              <li>
                <strong className="text-amber-400">Expired</strong> — 7 days pass without either threshold being hit.
                Expired is <strong>not</strong> a win, and it is counted against the hit rate below.
              </li>
            </ul>
            <p className="text-xs">
              Checks are daily closes-of-check, not intraday highs, so a spike between checks won't be claimed as a win.
              Once a pick resolves, the outcome is frozen — the ledger is append-only and outcomes are never re-graded.
              Hit rate = wins ÷ (wins + losses + expired).
            </p>
          </CardContent>
        </Card>

        {/* Scoreboard */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <StatBox label="Total picks" value={loading ? "—" : String(picks.length)} />
          <StatBox
            label="Hit rate"
            value={stats.hitRate == null ? "—" : `${stats.hitRate.toFixed(0)}%`}
            hint={`${stats.resolved} resolved`}
          />
          <StatBox label="Wins" value={String(stats.wins)} tone="text-green-400" />
          <StatBox label="Losses" value={String(stats.losses)} tone="text-red-400" />
          <StatBox
            label="Avg win"
            value={formatPct(stats.perf.avgWin)}
            tone="text-green-400"
            hint="resolved picks in profit"
          />
          <StatBox
            label="Avg loss"
            value={formatPct(stats.perf.avgLoss)}
            tone="text-red-400"
            hint="resolved picks in the red"
          />
          <StatBox
            label="Expectancy"
            value={formatPct(stats.perf.expectancy)}
            tone={(stats.perf.expectancy ?? 0) >= 0 ? "text-green-400" : "text-red-400"}
            hint="avg result per pick"
            title={EXPECTANCY_EXPLAINER}
          />
          <StatBox label="Open" value={String(stats.open)} hint={`${stats.expired} expired`} />
        </div>
        <p className="text-xs text-muted-foreground mb-8">{EXPECTANCY_EXPLAINER}</p>


        <Tabs defaultValue="all" className="w-full">
          <TabsList>
            <TabsTrigger value="all">All ({picks.length})</TabsTrigger>
            <TabsTrigger value="daily">Daily Picks ({dailyPicks.length})</TabsTrigger>
            <TabsTrigger value="gem">GEM Picks ({gemPicks.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6">
            <LedgerList rows={picks} loading={loading} />
          </TabsContent>
          <TabsContent value="daily" className="mt-6">
            <LedgerList rows={dailyPicks} loading={loading} />
          </TabsContent>
          <TabsContent value="gem" className="mt-6">
            <LedgerList rows={gemPicks} loading={loading} />
          </TabsContent>
        </Tabs>
      </main>

      <SiteFooter />
    </div>
  );
};

const StatBox = ({
  label,
  value,
  hint,
  tone,
  title,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: string;
  title?: string;
}) => (
  <Card className="border-border/60" title={title}>
    <CardContent className="py-4">
      <div className="type-eyebrow text-muted-foreground">{label}</div>
      <div className={`type-data text-2xl mt-1 ${tone ?? "text-foreground"}`}>{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
    </CardContent>
  </Card>
);


const fmtPrice = (v: number) => `A$${v.toFixed(v < 1 ? 6 : v < 10 ? 4 : 2)}`;

/**
 * Dev-only regression check: flags adjacent ledger rows that look like the same
 * pick logged twice (same token, same entry price, same publish day).
 */
const useDuplicateLedgerWarning = (rows: PickRow[]) => {
  useEffect(() => {
    if (!import.meta.env.DEV || rows.length < 2) return;
    const dayOf = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("en-AU") : "");
    for (let i = 1; i < rows.length; i++) {
      const a = rows[i - 1];
      const b = rows[i];
      if (
        a.token_symbol &&
        a.token_symbol === b.token_symbol &&
        Number(a.entry_price_aud) === Number(b.entry_price_aud) &&
        dayOf(a.published_at) === dayOf(b.published_at)
      ) {
        console.warn(
          `[track-record] Duplicate ledger entry detected: ${a.token_symbol} at the same entry price and publish day (${a.id}, ${b.id})`,
        );
      }
    }
  }, [rows]);
};

const LedgerList = ({ rows, loading }: { rows: PickRow[]; loading: boolean }) => {
  useDuplicateLedgerWarning(rows);

  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-12 text-center text-muted-foreground">
          <p>No entries yet. The ledger starts recording from the moment picks are published.</p>
          <p className="text-xs mt-2">
            This is intentional — we don't backfill history that wasn't append-only when it was written.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((row) => {
        const isRevision = row.revision_number > 1;
        const pct = row.current_pct;
        const winning = pct != null && pct > 0;

        return (
          <TokenCard
            key={row.id}
            symbol={row.token_symbol ?? "—"}
            className="border-border/60"
            rating={STATUS_LABEL[row.status] ?? row.status}
            ratingClassName={STATUS_STYLES[row.status] ?? ""}
            badges={
              <>
                <Badge variant="outline" className="text-xs uppercase tracking-wide">
                  {row.pick_type}
                </Badge>
                {isRevision && (
                  <Badge variant="secondary" className="text-xs">
                    Revision #{row.revision_number}
                  </Badge>
                )}
                {row.rating && (
                  <Badge variant="outline" className="text-xs">
                    {row.rating}
                  </Badge>
                )}
              </>
            }
            headerRight={
              <div className="type-caption text-right">
                Published {formatDate(row.published_at)}
                <div className="text-[10px]">
                  {row.resolved_at
                    ? `Resolved ${formatDate(row.resolved_at)}`
                    : row.last_checked_at
                      ? `Checked ${formatDate(row.last_checked_at)}`
                      : `Recorded ${formatDate(row.recorded_at)}`}
                </div>
              </div>
            }
            stats={[
              {
                label: "Entry score",
                value: row.entry_score != null ? Number(row.entry_score).toFixed(1) : "—",
              },
              {
                label: "Entry price",
                value: row.entry_price_aud
                  ? fmtPrice(Number(row.entry_price_aud))
                  : row.entry_price_usd
                    ? `US$${Number(row.entry_price_usd).toFixed(4)}`
                    : "—",
              },
              {
                label: "Best / worst",
                value:
                  row.peak_pct != null || row.trough_pct != null
                    ? `${row.peak_pct != null ? `${row.peak_pct > 0 ? "+" : ""}${Number(row.peak_pct).toFixed(1)}%` : "—"} / ${
                        row.trough_pct != null ? `${Number(row.trough_pct).toFixed(1)}%` : "—"
                      }`
                    : "—",
              },
              {
                label: row.status === "pending" ? "P&L vs entry (open)" : "P&L at resolution",
                tone: winning ? "text-green-400" : pct != null && pct < 0 ? "text-red-400" : undefined,
                value:
                  pct != null ? (
                    <span className="inline-flex items-center gap-1">
                      {winning ? (
                        <TrendingUp className="w-3.5 h-3.5" />
                      ) : pct < 0 ? (
                        <TrendingDown className="w-3.5 h-3.5" />
                      ) : null}
                      {Number(pct).toFixed(1)}%
                    </span>
                  ) : (
                    "—"
                  ),
              },
            ]}
            reasoning={
              row.payload?.thesis_text ? (
                <span className="text-xs line-clamp-2">{row.payload.thesis_text}</span>
              ) : null
            }
          >
            {isRevision && (
              <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                <ArrowUpRight className="w-3 h-3" />
                This entry revised an earlier version of the same pick — see prior revisions above.
              </p>
            )}
          </TokenCard>
        );
      })}
    </div>
  );
};


export default TrackRecord;
